import {
  isTenantWorkspaceHost,
  unknownWorkspaceNotFoundUrl,
} from '@/utils/saasPlatform'

const EXISTS_CACHE_PREFIX = 'clinch.workspaceHostExists:'
const PROBE_TIMEOUT_MS = 4000
const memoryExists = new Map()

function cacheKey(host) {
  return `${EXISTS_CACHE_PREFIX}${String(host || '').toLowerCase()}`
}

function readExistsCache(host) {
  if (memoryExists.get(host) === true) return true
  if (typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(cacheKey(host))
    if (raw === '1') {
      memoryExists.set(host, true)
      return true
    }
    return null
  } catch (err) {
    return null
  }
}

function writeExistsCache(host) {
  memoryExists.set(host, true)
  if (typeof sessionStorage === 'undefined') return
  try {
    sessionStorage.setItem(cacheKey(host), '1')
  } catch (err) {
    // Ignore quota / private-mode failures.
  }
}

/**
 * Probe whether the current wildcard host has a Domain row.
 * `true` = workspace exists, `false` = missing, `null` = unknown (fail open).
 *
 * Unknown hosts 404 at StrictTenantHostMiddleware before the view runs — treat
 * that 404 as missing. Network / 5xx must not bounce a live workspace.
 */
export async function workspaceHostExists(host) {
  const hostname = String(host || '').toLowerCase()
  if (!hostname) return null
  const cached = readExistsCache(hostname)
  if (cached === true) return true

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS)
  try {
    const url = `/api/public/workspaces/host-exists/?host=${encodeURIComponent(hostname)}`
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'omit',
      headers: {
        Accept: 'application/json',
        'X-Clinch-Tenant-Host': hostname,
      },
      signal: controller.signal,
    })
    if (response.status === 404) return false
    if (!response.ok) return null
    const data = await response.json()
    if (data && data.exists === true) {
      writeExistsCache(hostname)
      return true
    }
    if (data && data.exists === false) return false
    return null
  } catch (err) {
    return null
  } finally {
    clearTimeout(timer)
  }
}

export async function unknownWorkspaceRedirectUrl(loc) {
  const locationRef = loc || (typeof window !== 'undefined' ? window.location : null)
  const host = String(locationRef?.hostname || '').toLowerCase()
  if (!isTenantWorkspaceHost(host)) return ''
  const exists = await workspaceHostExists(host)
  if (exists !== false) return ''
  return unknownWorkspaceNotFoundUrl(host, locationRef)
}

export async function redirectIfUnknownWorkspaceHost(loc) {
  const dest = await unknownWorkspaceRedirectUrl(loc)
  if (!dest) return false
  const locationRef = loc || (typeof window !== 'undefined' ? window.location : null)
  if (!locationRef) return false
  const current = `${locationRef.origin}${locationRef.pathname}${locationRef.search}`
  if (current === dest) return false
  locationRef.replace(dest)
  return true
}
